// src/pages/Dashboard.tsx
import React from 'react';
import FilterSelect from '@/components/filters/FilterSelect';
import SummaryCard from '../../../components/cards/SummaryCard';
import LineChart from '../../../components/charts/LineChart';
import RecentMetricsTable from './components/RecentMetricsTable';
import { useDispatch } from 'react-redux';
import { fetchMyGamics, fetchMyGamicsAnalytics } from '@/store/Gamic';
import { fetchMyCampaigns, fetchMyCampaignsAnalytics } from '@/store/Campaign';
import { dayNames, monthNames } from '@/utils/Names';

const dataTypeOptions = [
  { value: 'Gamics', label: 'Data Type: Gamics' },
  { value: 'Campaign', label: 'Data Type: Campaign' },
];

const timeRangeOptions = [
  { value: 'yearly', label: 'Time Range: Yearly' },
  { value: 'monthly', label: 'Time Range: Monthly' },
  { value: 'weekly', label: 'Time Range: Weekly' },
  { value: 'daily', label: 'Time Range: Daily' },
];

type TimeRange = 'daily' | 'monthly' | 'yearly' | 'weekly';

interface SummaryItem {
  period: string;
  total: string;
  active: string;
  approved: string;
  pending: string;
  rejected: string;
}

interface RawSummaryData {
  previous: SummaryItem[];
  current: SummaryItem[];
}

type ChangeDirection = 'up' | 'down' | 'neutral';

interface SummaryData {
  label: string;
  value: number;
  pct: number;
  pctString: string;
  direction: ChangeDirection;
}

const Dashboard: React.FC = () => {
  const [dataType, setDataType] = React.useState('Gamics');
  const [timeRange, setTimeRange] = React.useState<TimeRange>('yearly');
  const [recentType, setRecentType] = React.useState('Gamics');
  const [rawSummaryData, setRawSummaryData] = React.useState<RawSummaryData>({
    previous: [],
    current: [],
  });
  const [recentRows, setRawData] = React.useState<any[]>([]);
  const [type, setType] = React.useState<'Gamic' | 'Campaign'>('Gamic');
  const dispatch = useDispatch<any>();

  // fetch gamic and campaign analytics data
  React.useEffect(() => {
    const fetchSummaryData = async () => {
      let res;
      if (dataType === 'Gamics') {
        res = await dispatch(fetchMyGamicsAnalytics({ timeRange }));
      } else if (dataType === 'Campaign') {
        res = await dispatch(fetchMyCampaignsAnalytics({ timeRange }));
      }

      const raw = res.payload.data; // length = 14
      const half = raw.length / 2; // 7

      const previous = raw.slice(0, half); // indices 0–6
      const current = raw.slice(half); // indices 7–13

      // ab dono vars ek object me daal ke state update karo
      setRawSummaryData({ previous, current });
    };
    fetchSummaryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataType, timeRange]);

  // Only generate chartData if data is available
  const chartData = React.useMemo(() => {
    const data = rawSummaryData.current;
    if (!data || data.length === 0) return [];

    return data.map((item) => {
      const d = new Date(item.period);

      switch (timeRange) {
        case 'yearly':
          return {
            label: monthNames[d.getMonth()],
            value: Number(item.total),
          };
        case 'monthly':
          return {
            label: d.getDate().toString(),
            value: Number(item.total),
          };
        case 'weekly':
          return {
            label: dayNames[d.getDay()],
            value: Number(item.total),
          };
        case 'daily': {
          const hour = d.getHours();
          return {
            label: `${hour}`,
            value: Number(item.total),
          };
        }
        default:
          // fallback
          return {
            label: item.period,
            value: Number(item.total),
          };
      }
    });
  }, [rawSummaryData, timeRange]);

  // Only generate summaryData if at least 2 entries are present
  const summaryData: SummaryData[] = React.useMemo(() => {
    const prevArr = rawSummaryData.previous;
    const currArr = rawSummaryData.current;
    if (prevArr.length === 0 || currArr.length === 0) return [];

    const keys = ['total', 'approved', 'pending', 'rejected'] as const;
    type DataKey = (typeof keys)[number];

    const keysC = ['total', 'active', 'approved', 'pending', 'rejected'] as const;
    type DataKeyC = (typeof keysC)[number];

    const labelsMap: Record<DataKey, string> = {
      total: 'Total Gamics',
      approved: 'Approved',
      pending: 'Pending',
      rejected: 'Rejected',
    };

    const labelsMapC: Record<DataKeyC, string> = {
      total: 'Total Campaigns',
      approved: 'Approved',
      active: 'Active',
      pending: 'Pending',
      rejected: 'Rejected',
    };

    const sumByKey = (arr: SummaryItem[], key: DataKey | DataKeyC) =>
      arr.reduce((acc, item) => acc + Number(item[key]), 0);

    const data = dataType === "Campaign" ? keysC : keys;

    return data.map((key) => {
      const prev = sumByKey(prevArr, key);
      const curr = sumByKey(currArr, key);

      let pct: number;
      let direction: ChangeDirection;

      if (curr > prev) {
        // Up
        direction = 'up';
        // Agar prev=0 aur curr>0, treat 100% up; warna normal formula
        pct = prev === 0 ? 100 : ((curr - prev) / prev) * 100;
      } else if (curr < prev) {
        // Down
        direction = 'down';
        // Agar prev=0 aur curr<prev (impossible curr<0), fallback 0
        pct = prev === 0 ? 0 : ((prev - curr) / prev) * 100;
      } else {
        // No change
        direction = 'neutral';
        pct = 0;
      }

      const pctRounded = Number(pct.toFixed(2));
      // formatted string with sign and % and arrow
      let pctString: string;
      if (direction === 'up') {
        pctString = `+${pctRounded}%`;
      } else if (direction === 'down') {
        pctString = `-${pctRounded}%`;
      } else {
        pctString = `0%`;
      }

      return {
        label:
          dataType === "Campaign"
            ? labelsMapC[key as DataKeyC]
            : labelsMap[key as DataKey],
        value: curr,
        pct: pctRounded,
        pctString,
        direction,
      };
    });
  }, [rawSummaryData]);

  // fetch gamic and campaign data

  React.useEffect(() => {
    const fetchData = async () => {
      let res;
      if (recentType === 'Gamics') {
        res = await dispatch(fetchMyGamics({}));
        setType('Gamic');
      } else if (recentType === 'Campaign') {
        res = await dispatch(fetchMyCampaigns({}));
        setType('Campaign');
      } else {
        return; // ya koi default behaviour
      }

      if (res.payload?.data) {
        setRawData(res.payload.data);
      } else {
        console.warn('No data returned for', recentType);
        setRawData([]);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentType, dispatch]);

  const columnClass = summaryData.length > 4 ? 'grid-cols-5' : 'grid-cols-4';

  return (
    <div className='space-y-8'>
      <div className=' p-[40px] rounded-[24px] bg-white space-y-[40px] '>
        {/* Header */}
        <div className='flex  bg-white items-center justify-between'>
          <h2 className='text-[32px] font-[700]'>Overview</h2>
          <div className='flex gap-4'>
            <FilterSelect
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
              options={dataTypeOptions}
            />

            <FilterSelect
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              options={timeRangeOptions}
            />
          </div>
        </div>

        {/* Summary Cards */}
        <div className={`grid bg-white ${columnClass} gap-4`}>
          {summaryData.map((d) => (
            <SummaryCard
              key={d.label}
              label={d.label}
              value={d.value}
              pctString={d.pctString}
              direction={d.direction}
            />
          ))}
        </div>

        {/* Chart */}
        <LineChart chartData={chartData} />
      </div>

      <div className='bg-white p-[40px] rounded-[24px] space-y-[40px] '>
        {/* Recents Metrics */}
        <div className='flex  bg-white items-center justify-between'>
          <h2 className='text-[32px] font-[700]'>Recents Metrics</h2>
          <FilterSelect
            value={recentType}
            onChange={(e) => setRecentType(e.target.value)}
            options={dataTypeOptions}
          />
        </div>
        <RecentMetricsTable rows={recentRows} type={type} />
      </div>
    </div>
  );
};

export default Dashboard;
